<template>
  <PageShell
    title="Inventory"
    subtitle="Lots on hand • Prep for shows with ShowTime!"
  >
    <div v-if="sellToast" class="toast">{{ sellToast }}</div>

    <template #actions>
      <router-link class="ll-pill ghost" to="/add-inventory">Add Inventory</router-link>
      <button class="ll-pill" type="button" @click="fetchInventory" :disabled="loading">
        {{ loading ? "Refreshing..." : "Refresh" }}
      </button>
      <button
        v-if="sellCart.lines.length"
        class="ll-pill"
        type="button"
        @click="openSellCart"
        title="Open Sales Draft"
      >
        Sell Cart • {{ sellCart.lines.length }} • ${{ fmtMoney(sellDraftRevenue) }}
      </button>
    </template>

    <div class="layout">
      <!-- Left: Inventory -->
      <section class="card">
        <div class="card-top">
          <div>
            <div class="card-title">On Hand</div>
            <div class="micro">
              Drag items into <b>ShowTime!</b> to prep for a card show.
            </div>
          </div>
        </div>

        <!-- Filters -->
        <div class="filters">
          <input class="input" v-model="q" placeholder="Search item, description, category…" />
          <select class="input" v-model="sortKey">
            <option value="name">Sort: Name</option>
            <option value="qty">Sort: Qty</option>
            <option value="updated">Sort: Last Updated</option>
            <option value="cost">Sort: Unit Cost</option>
          </select>
          <button class="ll-pill ghost" type="button" @click="clearFilters">Clear</button>
        </div>

        <!-- Desktop table -->
        <!-- Desktop list (compact, no column headers) -->
        <div class="list-wrap desktop-only">
          <div
            v-for="it in sortedFiltered"
            :key="it.key"
            class="inv-row"
            draggable="true"
            @dragstart="onDragStart(it, $event)"
          >
            <!-- Left -->
            <div class="inv-main">
              <div class="inv-top">
                <div class="inv-title">
                  <span class="strong">{{ it.item_name || it.item_no }}</span>

                  <!-- grade badge inline -->
                  <span
                    v-if="(it.graded ?? it.extras?.graded) === true"
                    class="badge"
                    title="Graded"
                  >
                    {{ (it.grade_authority ?? it.extras?.grade_authority) || "?" }}
                    {{ (it.grade ?? it.extras?.grade) || "" }}
                  </span>
                </div>

                <div class="inv-actions">
                  <button class="mini" type="button" @click="addToSellCart(it)">Sell It</button>
                  <button class="mini" type="button" @click="addToShowtime(it)">ShowTime!</button>
                  <button class="mini" type="button" @click="openAdjust(it)">Adjust</button>
                  <button class="mini danger" type="button" @click="confirmDelete(it)">Delete</button>
                </div>
              </div>

              <!-- optional description (1 line max) -->
              <div v-if="it.description" class="subtle clamp-1">
                {{ it.description }}
              </div>

              <!-- micro meta line -->
              <div class="micro subtle meta-line">
                <span>#{{ it.item_no }}</span>
                <span v-if="it.lot_id">• Lot {{ String(it.lot_id).slice(0, 8) }}</span>
                <span>• Qty {{ it.quantity_on_hand ?? 0 }}</span>
                <span>• ${{ fmtMoney(it.moving_avg_cost) }}</span>
                <span class="dim">• {{ fmtDate(it.last_updated) }}</span>
                <span v-if="it.category_name" class="dim">• {{ it.category_name }}</span>
              </div>

              <!-- compact card chips (no labels) -->
              <div class="chip-row tight" v-if="isCardRow(it)">
                <span v-if="it.year ?? it.extras?.year" class="chip sm">{{ it.year ?? it.extras?.year }}</span>
                <span v-if="(it.maker ?? it.extras?.maker)" class="chip sm">{{ it.maker ?? it.extras?.maker }}</span>
                <span v-if="(it.card_type ?? it.extras?.card_type)" class="chip sm">{{ it.card_type ?? it.extras?.card_type }}</span>
                <span v-if="(it.condition ?? it.extras?.condition)" class="chip sm">{{ it.condition ?? it.extras?.condition }}</span>

                <!-- if ungraded, show tiny "raw" chip -->
                <span v-if="(it.graded ?? it.extras?.graded) === false" class="chip sm ghost">raw</span>
              </div>
            </div>
          </div>

          <div v-if="!loading && sortedFiltered.length === 0" class="empty">
            No inventory matches your search.
          </div>
        </div>

        <!-- MOBILE cards (compact + pretty) -->
        <div class="mobile-only">
          <div class="mobile-list">
            <div
              v-for="it in sortedFiltered"
              :key="it.key"
              class="row-card compact"
              draggable="true"
              @dragstart="onDragStart(it, $event)"
            >
              <!-- Top row: title + grade badge + qty pill -->
              <div class="row-head">
                <div class="row-left">
                  <div class="inv-title">
                    <span class="strong">{{ it.item_name || it.item_no }}</span>

                    <!-- grade badge inline -->
                    <span
                      v-if="(it.graded ?? it.extras?.graded) === true"
                      class="badge"
                      title="Graded"
                    >
                      {{ (it.grade_authority ?? it.extras?.grade_authority) || "?" }}
                      {{ (it.grade ?? it.extras?.grade) || "" }}
                    </span>

                    <span
                      v-else-if="(it.graded ?? it.extras?.graded) === false && isCardRow(it)"
                      class="chip sm ghost"
                      title="Not graded"
                      style="margin-left: 6px;"
                    >
                      raw
                    </span>
                  </div>

                  <div v-if="it.description" class="subtle clamp-1">
                    {{ it.description }}
                  </div>
                </div>

                <div class="qty-pill">{{ it.quantity_on_hand ?? 0 }}</div>
              </div>

              <!-- Micro meta line (super compact) -->
              <div class="meta-line">
                <span>#{{ it.item_no }}</span>
                <span v-if="it.lot_id" class="dim">• Lot {{ String(it.lot_id).slice(0, 8) }}</span>
                <span class="dim">• ${{ fmtMoney(it.moving_avg_cost) }}</span>
                <span class="dim">• {{ fmtDate(it.last_updated) }}</span>
                <span v-if="it.category_name" class="dim">• {{ it.category_name }}</span>
              </div>

              <!-- Card jazz chips (label-less) -->
              <div class="chip-row tight" v-if="isCardRow(it)">
                <span v-if="it.year ?? it.extras?.year" class="chip sm">{{ it.year ?? it.extras?.year }}</span>
                <span v-if="(it.maker ?? it.extras?.maker)" class="chip sm">{{ it.maker ?? it.extras?.maker }}</span>
                <span v-if="(it.card_type ?? it.extras?.card_type)" class="chip sm">{{ it.card_type ?? it.extras?.card_type }}</span>
                <span v-if="(it.condition ?? it.extras?.condition)" class="chip sm">{{ it.condition ?? it.extras?.condition }}</span>
              </div>

              <!-- Actions -->
              <div class="btn-row">
                <button class="mini" type="button" @click="addToSellCart(it)">Sell It</button>
                <button class="mini" type="button" @click="addToShowtime(it)">ShowTime!</button>
                <button class="mini" type="button" @click="openAdjust(it)">Adjust</button>
                <button class="mini danger" type="button" @click="confirmDelete(it)">Delete</button>
              </div>

              <div class="drag-hint">Tip: press &amp; hold to drag into ShowTime!</div>
            </div>

            <div v-if="!loading && sortedFiltered.length === 0" class="empty-card">
              No inventory matches your search.
            </div>
          </div>
        </div>

      </section>

      
      <!-- Right: ShowTime! tray -->
      <aside
        class="card tray"
        @dragover.prevent
        @drop.prevent="onDropToShowtime"
      >
        <div class="tray-top">
          <div>
            <div class="card-title">ShowTime!</div>
            <div class="micro">
              Drag lots here to prep for a show. After the show, enter sold qty & price and we’ll book sales.
            </div>

            <!-- Session controls -->
            <div class="session-controls" v-if="currentSession">
              <select
                class="input"
                :value="showtimeState.currentSessionId"
                @change="onSessionPick($event.target.value)"
              >
                <option
                  v-for="s in showtimeState.sessions"
                  :key="s.session_id"
                  :value="s.session_id"
                >
                  {{ s.name }} — {{ s.show_date }}
                </option>
              </select>

              <div class="session-buttons">
                <button class="ll-pill" type="button" @click="newSession">New</button>
                <button class="ll-pill ghost" type="button" @click="renameSession">Rename</button>
                <button
                  class="ll-pill ghost danger"
                  type="button"
                  @click="removeSession"
                  :disabled="showtimeState.sessions.length <= 1"
                  title="Keep at least one session"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>

          <button
            class="ll-pill ghost"
            type="button"
            @click="clearShowtime"
            :disabled="showtime.length === 0"
          >
            Clear
          </button>
        </div>

        <div v-if="showtime.length === 0" class="tray-empty">
          Drop items here →
        </div>

        <div class="tray-list" v-else>
          <div v-for="row in showtime" :key="row.key" class="tray-item">
            <div class="tray-head">
              <div>
                <div class="strong">{{ row.item_name || row.item_no }}</div>
                <div class="subtle">{{ row.description || "—" }}</div>
                <div class="chip-row">
                  <span class="chip">ID: {{ row.item_no }}</span>
                  <span class="chip">On hand: {{ row.on_hand }}</span>
                </div>
              </div>
              <button class="mini danger" type="button" @click="removeFromShowtime(row.key)">
                Remove
              </button>
            </div>

            <div class="tray-grid">
              <label class="field">
                <div class="meta-label">Bring Qty</div>
                <input
                  class="input"
                  type="number"
                  min="1"
                  :max="row.on_hand"
                  :value="row.bring_qty"
                  @input="updateLine(row.key, { bring_qty: toNum($event.target.value) })"
                />
              </label>            
            </div>

            <!-- NEW: split pricing editor -->
            <div class="split-box">
              <div class="split-top">
                <div>
                  <div class="meta-label">Sold Splits</div>
                  <div class="micro">
                    Sold: <b>{{ soldQty(row) }}</b> / Bring: <b>{{ row.bring_qty }}</b>
                  </div>
                </div>

                <button class="ll-pill ghost" type="button" @click="addSplit(row.key)">
                  + Add split
                </button>
              </div>

              <div v-if="!row.splits || row.splits.length === 0" class="micro subtle" style="margin-top:6px;">
                Add split(s) to record different prices.
              </div>

              <div v-else class="split-list">
                <div v-for="s in row.splits" :key="s.id" class="split-row">
                  <label class="field">
                    <div class="meta-label">Qty</div>
                    <input
                      class="input"
                      type="number"
                      min="0"
                      :max="row.bring_qty"
                      :value="s.qty"
                      @input="updateSplit(row.key, s.id, { qty: toNum($event.target.value) })"
                    />
                  </label>

                  <label class="field">
                    <div class="meta-label">Price each</div>
                    <input
                      class="input"
                      type="number"
                      step="0.01"
                      min="0"
                      :value="s.price_each"
                      @input="updateSplit(row.key, s.id, { price_each: toNum($event.target.value) })"
                    />
                  </label>

                  <div class="field">
                    <div class="meta-label">Total</div>
                    <div class="strong">\${{ fmtMoney((s.qty || 0) * (s.price_each || 0)) }}</div>
                  </div>

                  <button class="mini danger" type="button" @click="removeSplit(row.key, s.id)">
                    Remove
                  </button>
                </div>
              </div>

              <div class="split-total" v-if="row.splits && row.splits.length">
                <div class="meta-label">Sold Total</div>
                <div class="strong">\${{ fmtMoney(soldRevenue(row)) }}</div>
              </div>
            </div>


            <div class="tray-actions">
              <button class="ll-pill ghost" type="button" @click="clearSplits(row.key)">
                Reset Sold
              </button>

              <button
                class="ll-pill"
                type="button"
                @pointerdown.capture="hardClickProbe"
                @click.capture="hardClickProbe"
                @click="bookShowtime"
                :disabled="false"
                title="HARD PROBE"
                style="position:relative; z-index:9999;"
              >
                HARD PROBE: Book Sales
              </button>


            </div>
          </div>
        </div>

        <!-- Tray footer (totals + preview) -->
        <div v-if="showtime.length > 0" class="tray-footer">
          <div class="strong">ShowTime Totals</div>
          <div class="micro">
            Planned bring: <b>{{ totalBringQty }}</b> •
            Sold: <b>{{ totalSoldQty }}</b> •
            Revenue: <b>\${{ fmtMoney(totalRevenue) }}</b>
          </div>

          <div class="tray-footer-actions">
            <button class="ll-pill ghost" type="button" @click="openShowtimePreview">
              Preview Booking
            </button>

            <button
              class="ll-pill"
              type="button"
              @click.stop="debugBookClick(); bookShowtime()"
              :disabled="!canBookSales || isBooking || isSessionBooked"
              :title="bookSalesTitle"
            >
              {{ isSessionBooked ? "Booked" : (isBooking ? "Booking..." : "Book Sales") }}
            </button>

          </div>
        </div>


        <div v-if="showtimeModal.open" class="ll-modal" @click.self="closeShowtimePreview">
          <div class="ll-modal-card">
            <div class="ll-modal-head">
              <div>
                <div class="strong">Book Sales Preview</div>
                <div class="micro">
                  Session: <b>{{ currentSession?.name }}</b> • Date: <b>{{ currentSession?.show_date }}</b>
                </div>
              </div>
              <button class="mini" type="button" @click="closeShowtimePreview">✕</button>
            </div>

            <div v-if="soldLines.length === 0" class="micro" style="margin-top:10px;">
              No sold items yet. Enter Sold Qty and Sold Price in ShowTime first.
            </div>

            <div v-else class="ll-modal-body">
              <div class="preview-grid">
                <div v-for="r in soldLines" :key="r.key" class="preview-row">
                  <div class="strong">{{ r.item_name || r.item_no }}</div>
                  <div class="micro">Qty: <b>{{ r.sold_qty }}</b> • Price: <b>\${{ fmtMoney(r.sold_price_each) }}</b></div>
                  <div class="strong">\${{ fmtMoney(r.sold_total) }}</div>
                </div>
              </div>

              <div class="preview-totals">
                <div class="micro">Units sold: <b>{{ previewCount }}</b></div>
                <div class="micro">Revenue: <b>\${{ fmtMoney(previewRevenue) }}</b></div>
              </div>
            </div>

            <div class="ll-modal-actions">
              <button class="ll-pill ghost" type="button" @click="closeShowtimePreview">Close</button>
              <button class="ll-pill" type="button"
                @click="finalizeSales"
                :disabled="isSelling || sellCart.lines.length === 0"
              >
                {{ isSelling ? "Booking..." : "Book Sales" }}
              </button>


            </div>
          </div>
        </div>
      </aside>

    </div>

    <!-- Adjust modal -->
    <div v-if="adjust.open" class="modal" @click.self="closeAdjust">
      <div class="modal-card">
        <div class="modal-title">Adjust Quantity</div>
        <div class="micro">This uses your existing PATCH route. For lot-based, we’ll later adjust at the lot level.</div>

        <div class="modal-grid">
          <div class="info">
            <div class="strong">{{ adjust.item.item_name || adjust.item.item_no }}</div>
            <div class="subtle">{{ adjust.item.description || "—" }}</div>
            <div class="chip-row">
              <span class="chip">Item No: {{ adjust.item.item_no }}</span>
              <span class="chip">On hand: {{ adjust.item.quantity_on_hand ?? 0 }}</span>
            </div>
          </div>

          <label class="field">
            <div class="meta-label">New Quantity On Hand</div>
            <input class="input" type="number" min="0" v-model.number="adjust.qty" />
          </label>
        </div>

        <div class="modal-actions">
          <button class="ll-pill ghost" type="button" @click="closeAdjust" :disabled="loading">Cancel</button>
          <button class="ll-pill" type="button" @click="submitAdjustment" :disabled="loading">
            {{ loading ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Sell Cart modal -->
      <div v-if="sellCart.open" class="ll-modal" @click.self="sellCart.open = false">
        <div class="ll-modal-card">
          <div class="ll-modal-head">
            <div>
              <div class="strong">Sell It</div>
              <div class="micro">Draft a sale from selected lots (eBay/WhatNot/etc).</div>
            </div>
            <button class="mini" type="button" @click="sellCart.open = false">✕</button>
          </div>

          <div class="ll-modal-body">
            <div class="filters" style="margin: 10px 0;">
              <select class="input" v-model="sellCart.platform">
                <option>eBay</option>
                <option>WhatNot</option>
                <option>Facebook</option>
                <option>Other</option>
              </select>

              <input class="input" type="date" v-model="sellCart.sale_date" />

              <input class="input" v-model="sellCart.buyer_name" placeholder="Buyer name (optional)" />
            </div>

            <div v-if="sellCart.lines.length === 0" class="micro">
              No items in Sell Cart yet.
            </div>

            <div v-else class="preview-grid">
              <div v-for="l in sellCart.lines" :key="l.key" class="preview-row">
                <div class="strong">{{ l.item_name || l.item_no }}</div>
                <div class="micro">
                  Lot {{ String(l.lot_id).slice(0, 8) }} • On hand: {{ l.on_hand }}
                </div>

                <div style="display:flex; gap:10px; align-items:center; justify-content:flex-end;">
                  <input class="input" style="width:90px;" type="number" min="1" :max="l.on_hand"
                    :value="l.qty_sold"
                    @input="l.qty_sold = Math.min(l.on_hand, Math.max(1, Number($event.target.value || 1)))"
                  />
                  <input class="input" style="width:110px;" type="number" step="0.01" min="0"
                    :value="l.unit_price"
                    @input="l.unit_price = Math.max(0, Number($event.target.value || 0))"
                    placeholder="Price"
                  />
                  <button class="mini danger" type="button"
                    @click="sellCart.lines = sellCart.lines.filter(x => x.key !== l.key)">
                    Remove
                  </button>
                </div>
              </div>
            </div>

            <div class="preview-totals" v-if="sellCart.lines.length">
              <div class="micro">
                Lines: <b>{{ sellCart.lines.length }}</b>
                • Units: <b>{{ sellCart.lines.reduce((a,x)=>a+Number(x.qty_sold||0),0) }}</b>
                • Revenue: <b>${{ fmtMoney(sellCart.lines.reduce((a,x)=>a+(Number(x.qty_sold||0)*Number(x.unit_price||0)),0)) }}</b>
              </div>
            </div>
          </div>

          <div class="ll-modal-actions">
            <button class="ll-pill ghost" type="button" @click="sellCart.open = false">Close</button>
            <button class="ll-pill danger" type="button"
              @click="sellCart.lines = []; sellCart.open = false;">
              Clear
            </button>

            <!-- later: hook this to POST /sales with allocations, or create draft modal review -->
            <button
              class="ll-pill"
              type="button"
              @click="finalizeSellCart"
              :disabled="isSelling || !sellCart.lines.length"
            >
              {{ isSelling ? "Saving..." : "Finalize Sales" }}
            </button>



          </div>
        </div>
      </div>


    <!-- Delete modal -->
    <div v-if="del.open" class="modal" @click.self="closeDelete">
      <div class="modal-card">
        <div class="modal-title">Delete Item</div>
        <div class="micro">
          You can only delete when quantity is 0.
        </div>

        <div class="danger-box">
          Delete <b>{{ del.item.item_no }}</b>? This cannot be undone.
        </div>

        <div class="modal-actions">
          <button class="ll-pill ghost" type="button" @click="closeDelete" :disabled="loading">Cancel</button>
          <button class="ll-pill danger" type="button" @click="deleteItem" :disabled="loading">
            {{ loading ? "Deleting..." : "Delete" }}
          </button>
        </div>
      </div>

      
      
    </div>

  </PageShell>
</template>

<script>
import api from "@/services/api";
import PageShell from "@/components/layout/PageShell.vue";

import {
  loadShowtimeState,
  createSession,
  setCurrentSession,
  updateSessionMeta,
  updateLines,
  deleteSession,
} from "@/services/showtimeStore";

export default {
  name: "InventoryPage",
  components: { PageShell },

  data() {
    return {
      loading: false,
      inventory: [],

      q: "",
      sortKey: "name",
      inventoryModule: "",


      // ShowTime! (local-only for now)
      // ShowTime! sessions (localStorage)
      showtimeState: loadShowtimeState(),
      dragPayload: null,
      showtimeModal: { open: false },
      isBooking: false,

      sellToast: "",

      sellCart: {
        open: false,
        platform: "eBay",
        sale_date: new Date().toISOString().slice(0, 10),
        buyer_name: "",
        buyer_email: "",
        tracking_number: "",
        carrier: "",
        total_fees: 0,
        total_taxes: 0,
        total_freight: 0,
        lines: []
      },
      isSelling: false,

    

      // Modals
      adjust: { open: false, item: null, qty: 0 },
      del: { open: false, item: null },
    };
  },
 

  computed: {
    normalizedInventory() {
      return this.inventory || [];
    },


    
    currentSession() {
      return (
        this.showtimeState.sessions.find(
          (s) => s.session_id === this.showtimeState.currentSessionId
        ) || null
      );
    },

    showtime() {
      return this.currentSession?.lines || [];
    },

    soldLines() {
      // Flatten: each split with qty>0 becomes a preview line
      const out = [];
      for (const row of (this.showtime || [])) {
        const splits = Array.isArray(row.splits) ? row.splits : [];
        for (const s of splits) {
          const qty = Number(s.qty || 0);
          const price = Number(s.price_each || 0);
          if (qty <= 0) continue;

          out.push({
            // unique key per split line
            key: `${row.key}::${s.id}`,
            item_no: row.item_no,
            item_name: row.item_name,
            description: row.description,

            lot_id: row.lot_id || null,
            module: row.module || null,

            sold_qty: qty,
            sold_price_each: price,
            sold_total: qty * price,
          });
        }
      }
      return out;
    },
    previewRevenue() {
      return this.soldLines.reduce((sum, r) => sum + Number(r.sold_total || 0), 0);
    },
    previewCount() {
      return this.soldLines.reduce((sum, r) => sum + Number(r.sold_qty || 0), 0);
    },

    previewLines() {
      // Keep for compatibility if referenced elsewhere
      return this.soldLines;
    },
    previewCostBasisTotal() {
      // Still 0 for now (COGS computed in DB on ship)
      return 0;
    },




    filtered() {
      const qq = (this.q || "").trim().toLowerCase();
      if (!qq) return this.normalizedInventory;

      return this.normalizedInventory.filter((it) => {
        const blob = [
          it.item_no,
          it.item_name,
          it.description,
          it.category_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return blob.includes(qq);
      });
    },

    sortedFiltered() {
      const arr = this.filtered.slice();

      const by = {
        name: (a, b) => (a.item_name || a.item_no).localeCompare(b.item_name || b.item_no),
        qty: (a, b) => (b.quantity_on_hand ?? 0) - (a.quantity_on_hand ?? 0),
        updated: (a, b) => String(b.last_updated || "").localeCompare(String(a.last_updated || "")),
        cost: (a, b) => (b.moving_avg_cost ?? 0) - (a.moving_avg_cost ?? 0),
      }[this.sortKey];

      return by ? arr.sort(by) : arr;
    },

    totalBringQty() {
      return this.showtime.reduce((s, r) => s + Number(r.bring_qty || 0), 0);
    },
    totalSoldQty() {
      return this.showtime.reduce((a, r) => a + this.soldQty(r), 0);
    },
    totalRevenue() {
      return this.showtime.reduce((a, r) => a + this.soldRevenue(r), 0);
    },

    bookingLines() {
      // Lines we will POST to /showtime/book
      // IMPORTANT: requires lot_id per row (lot-based selling)
      return (this.showtime || []).flatMap((row) => {
        const lotId = row.lot_id;
        const splits = Array.isArray(row.splits) ? row.splits : [];

        if (!lotId) return []; // cannot book without lot_id

        return splits
          .map((s) => ({
            lot_id: lotId,
            qty: Number(s.qty || 0),
            unit_price: Number(s.price_each || 0),
          }))
          .filter((x) => Number(x.qty) > 0);
      });
    },

    isSessionBooked() {
      return Boolean(this.currentSession?.booked_sale_id);
    },

    canBookSales() {
      // Must have at least one sold split AND all sold rows must be lot-based
      return this.bookingLines.length > 0 && !this.isSessionBooked;
    },

    bookSalesTitle() {
      if (this.isSessionBooked) return "Already booked for this session.";
      // This is the reality today because /inventory is item-level aggregate
      const needsLots = (this.showtime || []).some(r => this.soldQty(r) > 0 && !r.lot_id);
      if (needsLots) return "Cannot book yet: ShowTime rows need lot_id (book from lots, not item aggregate).";
      if (this.bookingLines.length === 0) return "Nothing sold yet. Enter qty + price (we'll auto-create a split if more than 1).";
      return "Create + ship this session now.";
    },

    sellDraftQty() {
      return (this.sellCart.lines || []).reduce((s, l) => s + Number(l.qty_sold || 0), 0);
    },
    sellDraftRevenue() {
      return (this.sellCart.lines || []).reduce(
        (s, l) => s + (Number(l.qty_sold || 0) * Number(l.unit_price || 0)),
        0
      );
    },



  },

  mounted() {
    this.fetchInventory();
  },

  methods: {
    clearFilters() {
      this.q = "";
      this.sortKey = "name";
    },
    
    fmtMoney(v) {
      const n = Number(v || 0);
      return n.toFixed(2);
    },

    fmtDate(v) {
      if (!v || v === "N/A") return "—";
      // supports ISO date strings or timestamps
      try {
        if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
          const [y, m, d] = v.slice(0, 10).split("-");
          return `${m}/${d}/${y}`;
        }
        return new Date(v).toLocaleDateString("en-US");
      } catch {
        return String(v);
      }
    },

    safeJsonParse(s) {
      if (typeof s !== "string") return {};
      try {
        return JSON.parse(s);
      } catch (err) {
        return {};
      }
    },

    openSellCart() {
      this.sellCart.open = true;
    },
    closeSellCart() {
      this.sellCart.open = false;
    },

   
    persistLines(lines) {
      const id = this.showtimeState.currentSessionId;
      this.showtimeState = updateLines(this.showtimeState, id, lines);
    },

    isCardRow(it) {
      if (!it) return false;

      // If backend uses Option A, this is the best signal
      const dm = String(it.display_module || "").toLowerCase();
      if (dm === "cards") return true;

      // Direct (top-level) fields
      const top = {
        year: it.year,
        maker: it.maker,
        condition: it.condition,
        card_type: it.card_type,
        graded: it.graded,
        grade: it.grade,
        grade_authority: it.grade_authority,
      };

      // Extras fields (stored in inventory_lots.extras jsonb)
      const ex = it.extras && typeof it.extras === "object" ? it.extras : {};

      const getStr = (v) => (v == null ? "" : String(v).trim());
      const has = (v) => getStr(v) !== "";

      const yearVal = top.year ?? ex.year;
      const gradedVal = top.graded ?? ex.graded;

      const hasAnyCardField =
        yearVal != null ||
        has(top.maker ?? ex.maker) ||
        has(top.condition ?? ex.condition) ||
        has(top.card_type ?? ex.card_type) ||
        gradedVal === true ||
        gradedVal === false ||
        has(top.grade ?? ex.grade) ||
        has(top.grade_authority ?? ex.grade_authority);

      // Also treat real module cards as cards even if empty extras
      const mod = String(it.module || "").toLowerCase();
      return mod === "cards" || hasAnyCardField;
    },


    async fetchInventory() {
      try {
        this.loading = true;

        const { data } = await api.get("/inventory-lots", {
          params: this.inventoryModule ? { module: this.inventoryModule } : undefined,
        });

        console.group("📦 fetchInventory() /inventory-lots response");
        console.log("inventoryModule:", this.inventoryModule);
        console.log("raw data:", data);
        console.log("data.lots is array?:", Array.isArray(data?.lots));
        console.log("lots length:", Array.isArray(data?.lots) ? data.lots.length : 0);
        if (Array.isArray(data?.lots) && data.lots.length) {
          console.log("first lot row:", data.lots[0]);
          console.log("first lot module:", data.lots[0]?.module);
          console.log("first lot extras type:", typeof data.lots[0]?.extras);
          console.log("first lot extras value:", data.lots[0]?.extras);

          console.log("first lot unit_cost:", data.lots[0]?.unit_cost);
          console.log("first lot qty_remaining:", data.lots[0]?.qty_remaining);
          console.log("first lot extras:", data.lots[0]?.extras);
        }
        console.groupEnd();

        const rows = Array.isArray(data?.lots) ? data.lots : [];
        const gregLotId = "677deb6f-b587-4100-be8d-fd010e08a8f2";
        const greg = rows.find(r => r.lot_id === gregLotId || r.item_no === "100031");

        console.log("🔎 Found Greg row?:", greg);
        console.log("🔎 Greg extras:", greg?.extras);
        console.log("🔎 Greg extracted fields:", {
          year: greg?.year,
          maker: greg?.maker,
          graded: greg?.graded,
          grade: greg?.grade,
          grade_authority: greg?.grade_authority,
          condition: greg?.condition,
          card_type: greg?.card_type,
          display_module: greg?.display_module,
        });


        // ✅ MAP lots → UI shape (so template fields exist)
        this.inventory = rows.map((l) => {
          const ex =
            l?.extras && typeof l.extras === "object"
              ? l.extras
              : (typeof l?.extras === "string" ? this.safeJsonParse(l.extras) : {});

          return {
            key: l.lot_id,

            lot_id: l.lot_id,
            module: l.module,
            display_module: l.display_module, // ✅ IMPORTANT for isCardRow()

            item_no: l.item_no,
            item_name: l.item_name,
            description: l.description,
            category_name: l.category_name,
            acquired_date: l.acquired_date,
            source: l.source,

            quantity_on_hand: Number(l.qty_remaining ?? 0),
            moving_avg_cost: Number(l.unit_cost ?? 0),
            last_updated: l.updated_at,

            // ✅ Prefer extracted SQL columns, fallback to extras
            year: l.year ?? ex.year ?? null,
            maker: l.maker ?? ex.maker ?? "",
            condition: l.condition ?? ex.condition ?? "",
            graded: l.graded ?? ex.graded ?? null,
            grade_authority: l.grade_authority ?? ex.grade_authority ?? "",
            grade: l.grade ?? ex.grade ?? "",
            card_type: l.card_type ?? ex.card_type ?? "",

            extras: ex,
          };
        });

        console.group("✅ MAPPED inventory sample");
        console.log("mapped length:", this.inventory.length);
        console.log("mapped[0]:", this.inventory[0]);
        console.log("mapped[0].module:", this.inventory[0]?.module);
        console.log("mapped[0].year:", this.inventory[0]?.year);
        console.log("mapped[0].maker:", this.inventory[0]?.maker);
        console.log("mapped[0].graded:", this.inventory[0]?.graded);
        console.log("mapped[0].grade_authority:", this.inventory[0]?.grade_authority);
        console.groupEnd();

      } catch (e) {
        console.error("❌ Error fetching inventory lots:", e);
        alert("Failed to load inventory lots.");
      } finally {
        this.loading = false;
      }
    },

    soldQty(row) {
      return (row?.splits || []).reduce((a, s) => a + Number(s.qty || 0), 0);
    },

    soldRevenue(row) {
      return (row?.splits || []).reduce(
        (a, s) => a + (Number(s.qty || 0) * Number(s.price_each || 0)),
        0
      );
    },

    // Drag / drop to ShowTime!
    onDragStart(it, ev) {
      this.dragPayload = it;

      const dt = ev?.dataTransfer;
      if (!dt) return;

      dt.effectAllowed = "copy";
      dt.setData("text/plain", String(it.item_no ?? ""));
    },

    addToSellCart(it) {
      console.group("addToSellCart() it:");
      console.log(it);

      const lot_id = it.lot_id;
      if (!lot_id) return alert("Missing lot_id.");

      const onHand = Number(it.quantity_on_hand ?? 0);

      // If already in cart, bump qty (clamped)
      const existing = this.sellCart.lines.find(x => x.lot_id === lot_id);
      if (existing) {
        existing.qty_sold = Math.min(onHand, Number(existing.qty_sold || 0) + 1);
        console.log("Sell cart updated. Lines:", this.sellCart.lines.length);
        console.groupEnd();
        return;
      }

      // Add new line (do NOT open modal)
      this.sellCart.lines.unshift({
        key: lot_id,
        lot_id,
        module: it.module || this.inventoryModule || "general", // ✅ add this
        item_no: it.item_no,
        item_name: it.item_name,
        description: it.description,
        on_hand: onHand,
        qty_sold: Math.min(1, onHand || 1),
        unit_price: 0,
      });

      this.sellToast = `Added to Sell Cart (${this.sellCart.lines.length})`;
      setTimeout(() => (this.sellToast = ""), 1200);


      console.log("Sell cart updated. Lines:", this.sellCart.lines.length);
      console.groupEnd();
    },


    createSaleDraft() {
      window.alert("Next step: POST /sales draft from sellCart.lines");
    },

    onDropToShowtime() {
      if (!this.dragPayload) return;
      this.addToShowtime(this.dragPayload);
      this.dragPayload = null;
    },

    addToShowtime(it) {
      const key = it.lot_id;
      if (!key) {
        alert("This row is missing lot_id. Cannot add to ShowTime.");
        return;
      }
      const existing = this.showtime.find((x) => x.key === key);
      if (existing) {
        const newLines = this.showtime.map((x) => {
          if (x.key !== key) return x;

          const updated = {
            ...x,
            bring_qty: Math.min(x.on_hand, Number(x.bring_qty || 0) + 1),
          };

          this.normalizeShowtimeRow(updated); // ✅ ADD THIS LINE

          return updated;
        });

        this.persistLines(newLines);
        return;
      }


      const newLines = [...this.showtime];
    
      //this.normalizeShowtimeRow(row); // ✅ ADD THIS LINE

      newLines.unshift({
        key,                   // lot_id
        lot_id: it.lot_id,
        module: it.module || this.inventoryModule || "general",

        item_no: it.item_no,
        item_name: it.item_name,
        description: it.description,

        on_hand: Number(it.quantity_on_hand ?? 0), // qty_remaining mapped into quantity_on_hand
        bring_qty: Math.min(1, Number(it.quantity_on_hand ?? 0)),

        splits: [],
      });


      this.persistLines(newLines);
    },

    removeFromShowtime(key) {
      this.persistLines(this.showtime.filter((x) => x.key !== key));
    },


    clearShowtime() {
      this.persistLines([]);
    },

    clamp(n, min, max) {
      const x = Number(n);
      if (!Number.isFinite(x)) return min;
      return Math.min(max, Math.max(min, x));
    },

    addSplit(key) {
      console.group("✅ addSplit fired");
      console.log("key:", key);

      const rows = (this.showtime || []).map((r) => {
        if (r.key !== key) return r;

        const splits = Array.isArray(r.splits) ? [...r.splits] : [];

        splits.push({
          id: String(Date.now()) + "-" + Math.random().toString(16).slice(2),
          qty: 0,
          price_each: 0,
        });

        const next = { ...r, splits };

        // IMPORTANT: normalize must NOT wipe splits
        this.normalizeShowtimeRow(next);

        return next;
      });

      this.persistLines(rows);

      console.log(
        "✅ after persistLines, splits len =",
        this.showtime?.find((x) => x.key === key)?.splits?.length
      );
      console.groupEnd();
    },


    updateSplit(key, splitId, patch) {
      const rows = this.showtime.map(r => {
        if (r.key !== key) return r;
        const splits = (r.splits || []).map(s =>
          s.id === splitId ? { ...s, ...patch } : s
        );
        const next = { ...r, splits };
        this.normalizeShowtimeRow(next);
        return next;
      });
      this.persistLines(rows);
    },

    removeSplit(key, splitId) {
      const rows = this.showtime.map(r => {
        if (r.key !== key) return r;
        const splits = (r.splits || []).filter(s => s.id !== splitId);
        const next = { ...r, splits };
        this.normalizeShowtimeRow(next);
        return next;
      });
      this.persistLines(rows);
    },

    clearSplits(key) {
      const rows = this.showtime.map(r => {
        if (r.key !== key) return r;
        const next = { ...r, splits: [] };
        this.normalizeShowtimeRow(next);
        return next;
      });
      this.persistLines(rows);
    },

    ensureDefaultSplit(row) {
      if (!row) return row;
      if (!Array.isArray(row.splits)) row.splits = [];

      // If user hasn't added splits yet, create one blank split
      if (row.splits.length === 0) {
        row.splits.push({
          id: String(Date.now()) + "-" + Math.random().toString(16).slice(2),
          qty: 0,
          price_each: 0,
        });
      }

      return row;
    },


    normalizeShowtimeRow(row) {
      if (!row) return row;

      row.on_hand = Math.max(0, Number(row.on_hand ?? 0));
      row.bring_qty = Math.min(row.on_hand, Math.max(1, Number(row.bring_qty ?? 1)));

      if (!Array.isArray(row.splits)) row.splits = [];

      // normalize each split
      row.splits = row.splits.map(s => ({
        id: s.id ?? (crypto?.randomUUID?.() || String(Date.now() + Math.random())),
        qty: Math.max(0, Math.floor(Number(s.qty ?? 0))),
        price_each: Math.max(0, Number(s.price_each ?? 0)),
      }));

      // clamp total sold to bring_qty (trim from the end)
      let totalSold = row.splits.reduce((a, s) => a + s.qty, 0);
      if (totalSold > row.bring_qty) {
        let over = totalSold - row.bring_qty;
        for (let i = row.splits.length - 1; i >= 0 && over > 0; i--) {
          const take = Math.min(over, row.splits[i].qty);
          row.splits[i].qty -= take;
          over -= take;
        }
      }

      // drop empty splits (optional)
    //  row.splits = row.splits.filter(s => s.qty > 0 || s.price_each > 0);

      return row;
    },
    
    onSessionPick(id) {
      this.showtimeState = setCurrentSession(this.showtimeState, id);
    },

    newSession() {
      const name = prompt("Session name?", "New Show");
      if (name === null) return;
      this.showtimeState = createSession(this.showtimeState, { name });
    },

    renameSession() {
      if (!this.currentSession) return;
      const name = prompt("Rename session:", this.currentSession.name);
      if (name === null) return;
      this.showtimeState = updateSessionMeta(
        this.showtimeState,
        this.currentSession.session_id,
        { name }
      );
    },

    removeSession() {
      if (!this.currentSession) return;
      const ok = confirm(`Delete session "${this.currentSession.name}"?`);
      if (!ok) return;
      this.showtimeState = deleteSession(this.showtimeState, this.currentSession.session_id);
    },



    // Adjust
    openAdjust(it) {
      this.adjust.open = true;
      this.adjust.item = it;
      this.adjust.qty = Number(it.quantity_on_hand ?? 0);
    },
    closeAdjust() {
      this.adjust.open = false;
      this.adjust.item = null;
      this.adjust.qty = 0;
    },
    async submitAdjustment() {
      if (!this.adjust.item) return;
      try {
        this.loading = true;
        await api.patch(`/inventory/${this.adjust.item.item_no}`, {
          quantity_on_hand: this.adjust.qty,
        });
        this.closeAdjust();
        await this.fetchInventory();
      } catch (e) {
        console.error("❌ Error adjusting inventory:", e);
        alert("Failed to adjust inventory.");
      } finally {
        this.loading = false;
      }
    },

    // Delete
    confirmDelete(it) {
      const qty = Number(it.quantity_on_hand ?? 0);
      if (qty > 0) {
        alert("❌ Cannot Delete Item! Quantity On Hand must be 0.\nAdjust quantity to zero first.");
        return;
      }
      this.del.open = true;
      this.del.item = it;
    },
    closeDelete() {
      this.del.open = false;
      this.del.item = null;
    },
    async deleteItem() {
      if (!this.del.item) return;
      try {
        this.loading = true;
        await api.delete(`/inventory/${this.del.item.item_no}`);
        this.closeDelete();
        await this.fetchInventory();
      } catch (e) {
        console.error("❌ Error deleting item:", e);
        alert("Failed to delete item.");
      } finally {
        this.loading = false;
      }
    },

    toNum(v) {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },

    updateLine(key, patch) {
      const lines = this.showtime.map((x) => {
        if (x.key !== key) return x;

        const next = { ...x, ...patch };

        // Clamp rules (nice UX)
        next.bring_qty = Math.max(1, Math.min(Number(next.bring_qty || 1), Number(next.on_hand || 0)));
        next.sold_qty = Math.max(0, Math.min(Number(next.sold_qty || 0), Number(next.bring_qty || 0)));
        next.sold_price_each = Math.max(0, Number(next.sold_price_each || 0));

        return next;
      });

      this.persistLines(lines);
    },

    openShowtimePreview() {
      this.showtimeModal.open = true;
    },

    closeShowtimePreview() {
      this.showtimeModal.open = false;
    },

    
    closeSession() {
      if (!this.currentSession) return;

      const ok = confirm(`Close session "${this.currentSession.name}"? You can still edit later, but it marks it as done.`);
      if (!ok) return;

      this.showtimeState = updateSessionMeta(
        this.showtimeState,
        this.currentSession.session_id,
        { status: "closed" }
      );

      // pop preview immediately
      this.openShowtimePreview();
    },

    getShowtimeSaleDateISO() {
      // Prefer session show_date (your session dropdown displays show_date)
      // If show_date is "YYYY-MM-DD", make it stable by converting to noon UTC (prevents date shifting).
      const d = this.currentSession?.show_date;
      if (!d) return new Date().toISOString();

      if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
        return new Date(`${d}T12:00:00.000Z`).toISOString();
      }

      // Otherwise assume it's parseable
      const dt = new Date(d);
      return Number.isNaN(dt.getTime()) ? new Date().toISOString() : dt.toISOString();
    },

    async bookShowtime() {
      console.group("🟣 bookShowtime() ENTER");
      console.log("isBooking:", this.isBooking);
      console.log("canBookSales:", this.canBookSales);
      console.log("bookSalesTitle:", this.bookSalesTitle);
      console.log("isSessionBooked:", this.isSessionBooked);
      console.log("bookingLines:", this.bookingLines);
      console.groupEnd();

      if (this.isBooking) return;

      // Auto-create default split for single-price sales
      const patched = (this.showtime || []).map(r => {
        // If they haven't added splits but they intended to sell (bring>0),
        // ensure there's at least one split so they can just fill qty/price.
        const next = { ...r, splits: Array.isArray(r.splits) ? [...r.splits] : [] };
        this.ensureDefaultSplit(next);
        this.normalizeShowtimeRow(next);
        return next;
      });

      // Persist only if anything changed (i.e. some row had no splits)
      const changed = (this.showtime || []).some(r => !Array.isArray(r.splits) || r.splits.length === 0);
      if (changed) this.persistLines(patched);

      if (!this.canBookSales) {
        alert(this.bookSalesTitle);
        return;
      }


      const ok = confirm(
        `Book and ship ShowTime session "${this.currentSession?.name}" now?\n\nThis will create a Sale and immediately consume lots.`
      );
      if (!ok) return;

      this.isBooking = true;

      try {
        const payload = {
          module: (this.showtime?.[0]?.module || "general"),
          sale_date: this.getShowtimeSaleDateISO(),
          session_id: this.currentSession.session_id,
          session_name: this.currentSession.name,
          buyer_name: "ShowTime Walk-Up",
          platform: "ShowTime",
          lines: this.bookingLines,
        };

        console.group("📦 ShowTime booking payload");
        console.log(payload);
        console.groupEnd();

        
        const { data } = await api.post("/showtime/book", payload);

        console.group("✅ /showtime/book response");
        console.log(data);
        console.groupEnd();


        // Mark session as booked locally so user can’t accidentally re-book
        this.showtimeState = updateSessionMeta(
          this.showtimeState,
          this.currentSession.session_id,
          {
            booked_sale_id: data?.sale_id || null,
            booked_at: new Date().toISOString(),
            invoice_number: data?.invoice_number || null,
            status: "booked",
          }
        );

        // Refresh authoritative inventory
        await this.fetchInventory();

        // Optional: open preview after booking
        this.openShowtimePreview();

        alert(`✅ Booked successfully! Sale ID: ${data?.sale_id}`);
      } catch (e) {
        console.error("❌ Error booking ShowTime:", e);

        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to book ShowTime session.";

        // If backend returns 409 with extra.sale_id, surface it
        const extraSaleId = e?.response?.data?.extra?.sale_id;

        if (extraSaleId) {
          alert(`⚠️ ${msg}\nExisting sale_id: ${extraSaleId}`);
        } else {
          alert(`❌ ${msg}`);
        }
      } finally {
        this.isBooking = false;
      }
    },

    
    
    finalizeSalesDraftPayload() {
      // Lines must have qty + price and must have lot_id (since inventory is lot-based here)
      const lines = (this.sellCart.lines || [])
        .map(l => {
          const qty = Number(l.qty_sold || 0);
          const price = Number(l.unit_price || 0);

          return {
            item_no: String(l.item_no),
            quantity_sold: qty,
            unit_price: price,
            description: l.description || null,

            // consume the exact lot the user selected into the cart
            module: l.module || this.inventoryModule || "general",
            allocations: [{ lot_id: l.lot_id, qty }],
          };
        })
        .filter(x => x.quantity_sold > 0);

      return {
        module: this.inventoryModule || "general",
        sale_date: this.sellCart.sale_date ? new Date(`${this.sellCart.sale_date}T12:00:00.000Z`).toISOString() : new Date().toISOString(),
        buyer_name: this.sellCart.buyer_name || null,
        buyer_email: this.sellCart.buyer_email || null,
        platform: this.sellCart.platform || null,
        tracking_number: this.sellCart.tracking_number || null,
        carrier: this.sellCart.carrier || null,
        total_fees: Number(this.sellCart.total_fees || 0),
        total_taxes: Number(this.sellCart.total_taxes || 0),
        total_freight: Number(this.sellCart.total_freight || 0),
        lines,
      };
    },

    validateSellCartForCheckout() {
      if (!Array.isArray(this.sellCart.lines) || this.sellCart.lines.length === 0) {
        return "Cart is empty.";
      }

      for (const l of this.sellCart.lines) {
        const qty = Number(l.qty_sold || 0);
        const price = Number(l.unit_price || 0);

        if (!l.lot_id) return `Missing lot_id for item ${l.item_no}.`;
        if (!l.item_no) return `Missing item_no for a cart line.`;
        if (!Number.isFinite(qty) || qty <= 0) return `Qty sold must be > 0 for item ${l.item_no}.`;
        if (!Number.isFinite(price) || price < 0) return `Unit price must be >= 0 for item ${l.item_no}.`;
      }

      return null;
    },

    async finalizeSellCart() {
      if (this.isSelling) return;

      if (!this.sellCart.lines.length) {
        alert("No items in cart.");
        return;
      }

      // Must have lot_id + item_no + qty_sold
      const bad = this.sellCart.lines.find(l =>
        !l.lot_id ||
        !String(l.item_no || "").trim() ||
        !Number(l.qty_sold) ||
        Number(l.qty_sold) <= 0
      );

      if (bad) {
        alert("Each line must have lot_id, item_no, and qty_sold > 0.");
        return;
      }

      const ok = confirm(
        "Finalize sale now?\n\nThis will create the Sale, consume inventory lots, compute COGS, and mark shipped."
      );
      if (!ok) return;

      this.isSelling = true;

      try {
        // Convert YYYY-MM-DD -> ISO at noon UTC to avoid timezone shift
        const saleDateISO = this.sellCart.sale_date
          ? `${this.sellCart.sale_date}T12:00:00.000Z`
          : new Date().toISOString();

        const payload = {
          // You can leave module off entirely, since each line includes module.
          // module: "general",

          sale_date: saleDateISO,

          buyer_name: this.sellCart.buyer_name || null,
          buyer_email: this.sellCart.buyer_email || null,
          platform: this.sellCart.platform || "eBay",
          tracking_number: this.sellCart.tracking_number || null,
          carrier: this.sellCart.carrier || null,

          total_fees: Number(this.sellCart.total_fees || 0),
          total_taxes: Number(this.sellCart.total_taxes || 0),
          total_freight: Number(this.sellCart.total_freight || 0),

          // ✅ This is the critical fix: include item_no + allocations
          lines: this.sellCart.lines.map(l => {
            const qty = Number(l.qty_sold);

            return {
              item_no: String(l.item_no).trim(),
              quantity_sold: qty,
              unit_price: Number(l.unit_price || 0),
              description: l.description || null,

              // IMPORTANT: must match the lot.module in inventory_lots for allocation precheck
              module: String(l.module || "general").trim() || "general",

              allocations: [
                { lot_id: l.lot_id, qty }
              ]
            };
          })
        };

        console.group("🧾 /sales/checkout payload");
        console.log(payload);
        console.groupEnd();

        const { data } = await api.post("/sales/checkout", payload);

        alert(`✅ Sale booked + shipped. Sale ID: ${data.sale_id}`);

        // Clear cart and refresh UI
        this.sellCart.lines = [];
        this.sellCart.open = false;
        await this.fetchInventory();

      } catch (e) {
        console.error("❌ finalizeSellCart error:", e);

        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to finalize sale.";

        // if you ever include pg metadata in responses, this helps:
        console.log("Server response:", e?.response?.data);

        alert(`❌ ${msg}`);
      } finally {
        this.isSelling = false;
      }
    },


    async finalizeSales() {
      const problem = this.validateSellCartForCheckout();
      if (problem) return alert(problem);

      const ok = confirm("Book these sales now? This will consume lots and mark the sale as shipped.");
      if (!ok) return;

      this.isSelling = true;

      try {
        const payload = this.finalizeSalesDraftPayload();

        console.group("🧾 /sales/checkout payload");
        console.log(payload);
        console.groupEnd();

        const { data } = await api.post("/sales/checkout", payload);

        console.log("✅ /sales/checkout response:", data);

        // clear cart + close modal
        this.sellCart.lines = [];
        this.sellCart.open = false;

        // refresh inventory (lots are the source of truth)
        await this.fetchInventory();

        alert(`✅ Booked! Sale ID: ${data?.sale_id}`);
      } catch (e) {
        console.error("❌ finalizeSales error:", e);

        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to book sales.";

        alert(`❌ ${msg}`);
      } finally {
        this.isSelling = false;
      }
    },



    debugBookClick() {
      console.group("🟣 Book Sales CLICK");
      console.log("canBookSales:", this.canBookSales);
      console.log("isBooking:", this.isBooking);
      console.log("isSessionBooked:", this.isSessionBooked);
      console.log("bookSalesTitle:", this.bookSalesTitle);
      console.log("currentSession:", this.currentSession);
      console.log("showtime[0]:", this.showtime?.[0]);
      console.log("bookingLines len:", Array.isArray(this.bookingLines) ? this.bookingLines.length : "not array");
      console.groupEnd();
    },

    debugMissingLots() {
      const bad = (this.showtime || []).filter(r => this.soldQty(r) > 0 && !r.lot_id);
      console.group("🚫 Sold rows missing lot_id");
      console.log("count:", bad.length);
      console.table(bad.map(r => ({
        key: r.key,
        item_no: r.item_no,
        lot_id: r.lot_id,
        sold: this.soldQty(r),
        module: r.module
      })));
      console.groupEnd();
    },

  },
};
</script>

<style scoped>
.layout {
  display: grid;
  grid-template-columns: 1.7fr 1fr;
  gap: 12px;
  align-items: start;
}

.card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  padding: 14px;
}

.card-title {
  font-weight: 900;
  font-size: 14px;
  letter-spacing: 0.2px;
  margin-bottom: 4px;
}

.micro {
  font-size: 12px;
  opacity: 0.75;
  font-weight: 700;
  line-height: 1.25rem;
}

.card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.filters {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1fr 220px auto;
  gap: 10px;
}

.input {
  width: 100%;
  min-width: 0;
  padding: 10px 10px;
  border-radius: 12px;
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: #f6fbff;
  font-weight: 700;
  font-size: 16px;
  box-sizing: border-box;
}

.input:focus {
  outline: none;
  border-color: rgba(50, 123, 168, 0.55);
}

/* =========================================
   NEW: Compact desktop list (no headings)
   ========================================= */
.list-wrap {
  margin-top: 10px;
  display: grid;
  gap: 10px;
}

.inv-row {
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  padding: 12px;
}

.inv-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.inv-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.badge {
  font-size: 12px;
  font-weight: 900;
  opacity: 0.9;
  background: rgba(40, 134, 113, 0.12);
  border: 1px solid rgba(40, 134, 113, 0.35);
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.inv-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.meta-line {
  margin-top: 6px;
  font-size: 12px;
  opacity: 0.75;
  font-weight: 800;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.meta-dot {
  opacity: 0.55;
  font-weight: 900;
}

.dim {
  opacity: 0.75;
}

/* clamp description to one line (no giant rows) */
.clamp-1 {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}


/* tighten chips for card “jazz” */
.chip-row.tight {
  margin-top: 8px;
  gap: 6px;
}

.chip.sm {
  padding: 4px 8px;
  font-size: 12px;
}

.chip.ghost {
  opacity: 0.65;
}

/* =========================================
   Existing table styles (kept for reference)
   (You can delete these if table is removed)
   ========================================= */
.table-wrap {
  margin-top: 10px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.86);
}

.grid {
  width: 100%;
  border-collapse: collapse;
}

.grid th,
.grid td {
  border-bottom: 1px solid #e6eef3;
  padding: 10px;
  text-align: left;
  vertical-align: top;
}

.grid th {
  background: #e6eef3;
  font-weight: 900;
  font-size: 12.5px;
}

.strong {
  font-weight: 900;
}

.subtle {
  margin-top: 2px;
  font-size: 12px;
  opacity: 0.75;
  font-weight: 700;
  line-height: 1.2rem;
}

.chip-row {
  margin-top: 6px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.chip {
  font-size: 12px;
  font-weight: 900;
  opacity: 0.8;
  background: rgba(230, 238, 243, 0.9);
  border: 1px solid rgba(176, 196, 222, 0.9);
  padding: 6px 8px;
  border-radius: 999px;
}

.btn-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.mini {
  border-radius: 999px;
  border: 1px solid rgba(176, 196, 222, 0.9);
  background: #f6fbff;
  font-weight: 900;
  padding: 8px 10px;
  cursor: pointer;
}

.mini:hover {
  background: rgba(230, 238, 243, 0.8);
}

.mini.danger {
  border-color: rgba(159, 50, 7, 0.35);
  background: rgba(255, 246, 241, 0.85);
}

.mini.danger:hover {
  background: rgba(255, 236, 226, 0.95);
}

.empty {
  text-align: center;
  padding: 18px;
  opacity: 0.75;
  font-weight: 800;
}

.desktop-only { display: block; }
.mobile-only { display: none; }

/* Mobile cards */
.mobile-list {
  margin-top: 10px;
  display: grid;
  gap: 10px;
}

.row-card {
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  padding: 12px;
  display: grid;
  gap: 10px;
}

.row-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.qty-pill {
  min-width: 44px;
  height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  background: #288671;
  color: white;
  box-shadow: 0px 6px 14px rgba(40, 134, 113, 0.25);
}

.meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.meta-label {
  font-weight: 900;
  font-size: 12px;
  opacity: 0.7;
  margin-bottom: 6px;
}

.drag-hint {
  font-size: 12px;
  opacity: 0.7;
  font-weight: 700;
}

/* ShowTime tray */
.tray {
  position: sticky;
  top: 12px;
}

.tray-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.tray-empty {
  margin-top: 10px;
  border-radius: 16px;
  border: 1px dashed rgba(131, 167, 203, 0.95);
  padding: 18px;
  font-weight: 900;
  opacity: 0.75;
  text-align: center;
  background: rgba(246, 251, 255, 0.9);
}

.tray-list {
  margin-top: 10px;
  display: grid;
  gap: 10px;
}

.tray-item {
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.88);
  padding: 12px;
  display: grid;
  gap: 10px;
}

.tray-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.tray-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.tray-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.tray-footer {
  margin-top: 10px;
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(246, 251, 255, 0.9);
  padding: 12px;
}

.modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 14px;
}

.modal-card {
  width: min(720px, 96vw);
  border-radius: 18px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0px 18px 40px rgba(0, 0, 0, 0.25);
  padding: 14px;
}

.modal-title {
  font-weight: 900;
  font-size: 16px;
  margin-bottom: 6px;
}

.modal-grid {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 12px;
  align-items: start;
}

.modal-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.danger-box {
  margin-top: 10px;
  border-radius: 16px;
  border: 1px solid rgba(159, 50, 7, 0.35);
  background: rgba(255, 246, 241, 0.9);
  padding: 12px;
  font-weight: 800;
}

:deep(.ll-pill) { white-space: nowrap; }
:deep(.ll-pill.danger) {
  background: #9f3207;
  color: white;
  border: 1px solid rgba(159, 50, 7, 0.25);
}
:deep(.ll-pill.danger:hover) {
  background: #7d2705;
}

.session-controls {
  margin-top: 10px;
  display: grid;
  gap: 10px;
}

.session-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
}

.ll-modal{
  position:fixed;
  inset:0;
  background: rgba(0,0,0,0.45);
  display:flex;
  align-items:center;
  justify-content:center;
  padding: 14px;
  z-index: 9999;
}
.ll-modal-card{
  width: min(720px, 100%);
  background: rgba(255,255,255,0.92);
  border: 1px solid rgba(131,167,203,0.85);
  border-radius: 18px;
  box-shadow: 0px 10px 28px rgba(0,0,0,0.18);
  padding: 14px;
}
.ll-modal-head{
  display:flex;
  justify-content: space-between;
  align-items:flex-start;
  gap:10px;
}
.ll-modal-body{ margin-top: 10px; }
.preview-grid{
  display:grid;
  gap: 10px;
}
.preview-row{
  border: 1px solid rgba(176,196,222,0.85);
  background: #f6fbff;
  border-radius: 14px;
  padding: 10px;
  display:flex;
  justify-content: space-between;
  gap: 10px;
}
.preview-row > div:nth-child(1){
  flex: 1 1 auto;
}
.preview-totals{
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(176,196,222,0.6);
  display:flex;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.ll-modal-actions{
  margin-top: 12px;
  display:flex;
  gap:10px;
}
.ll-modal-actions .ll-pill{
  flex: 1 1 160px;
}

.tray-footer{
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(131, 167, 203, 0.65);
}

.tray-footer-actions{
  margin-top: 10px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.tray-footer-actions .ll-pill{
  flex: 1 1 180px;
}

.modal-backdrop{
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  z-index: 9999;
}
.modal{
  width: min(720px, 100%);
  background: rgba(255,255,255,0.92);
  border: 1px solid rgba(131,167,203,0.85);
  border-radius: 18px;
  box-shadow: 0px 10px 28px rgba(0,0,0,0.2);
  padding: 14px;
}
.modal-head{
  display:flex;
  align-items:flex-start;
  justify-content: space-between;
  gap: 10px;
}
.modal-title{
  font-weight: 900;
  font-size: 18px;
}
.preview-list{
  margin-top: 10px;
  display: grid;
  gap: 10px;
}
.preview-row{
  display:flex;
  justify-content: space-between;
  gap: 12px;
  background: #f6fbff;
  border: 1px solid rgba(176,196,222,0.85);
  border-radius: 14px;
  padding: 10px;
}
.preview-row .right{
  text-align:right;
}
.modal-foot{
  margin-top: 12px;
  display:flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.split-box {
  margin-top: 10px;
  padding: 10px;
  border-radius: 14px;
  border: 1px solid rgba(131, 167, 203, 0.5);
  background: rgba(255, 255, 255, 0.55);
}

.split-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-start;
}

.split-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 10px;
  align-items: end;
  margin-top: 10px;
}

.split-total {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

/* MOBILE compact row tweaks */
.row-card.compact{
  gap: 8px;
  padding: 12px;
}

.row-left{ min-width: 0; }

.row-card.compact .inv-title{
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.row-card.compact .meta-line{
  margin-top: 2px;
  font-size: 12px;
  opacity: 0.78;
  font-weight: 800;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.row-card.compact .clamp-1 {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}


.row-card.compact .chip-row.tight{
  margin-top: 4px;
  gap: 6px;
}

.row-card.compact .chip.sm{
  padding: 4px 8px;
  font-size: 12px;
}

.row-card.compact .chip.ghost{
  opacity: 0.65;
}


@media (max-width: 980px) {
  .layout {
    grid-template-columns: 1fr;
  }
  .tray {
    position: static;
  }
  .filters {
    grid-template-columns: 1fr;
  }
  .desktop-only { display: none; }
  .mobile-only { display: block; }

  .meta-grid {
    grid-template-columns: 1fr;
  }
  .tray-grid {
    grid-template-columns: 1fr;
  }
  .tray-actions {
    grid-template-columns: 1fr;
  }
  .modal-grid {
    grid-template-columns: 1fr;
  }
  .split-row {
    grid-template-columns: 1fr;
  }
  .modal-actions {
    flex-direction: column;
  }
  :deep(.ll-pill) { width: 100%; }

  /* Mobile: make compact row breathe a bit */
  .inv-top { flex-direction: column; }
  .inv-actions { justify-content: flex-start; }
}
</style>
